import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

import { Lock } from "@assets/svg/Lock";
import { ErrorComponent } from "@components/ErrorComponent";
import { TextInput } from "@components/Input";
import { Loader } from "@components/Loader";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { SsnDisclaimer } from "@components/SsnDisclaimer";
import { TextHighlighter } from "@components/TextHighlighter";
import { BodySmall, Caption, H2 } from "@components/Typography";
import { UserTaxIdType } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { useErrorBanner, useOnboarding, useSetUserTaxProfile, useTheme, useUser } from "@hooks";
import { Translations, i18n } from "@i18n";
import { constants } from "@layout";
import { formatSsnNumber } from "@utils";
import { validationSchema } from "./content";
import { ssnRouteStyles } from "./styles";
import { SsnFormValues } from "./types";

export const SsnRoute = () => {
  const { theme } = useTheme();
  const { resetToNext } = useOnboarding();

  const { showErrorBanner } = useErrorBanner();

  const { user, loading: userLoading, error: userError } = useUser();
  const [setUserTaxProfile, { loading }] = useSetUserTaxProfile();

  const {
    register,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid },
  } = useForm<SsnFormValues>({
    defaultValues: {
      ssn: "",
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const ssnController = register("ssn");

  const onChangeText = (value: string) => {
    const formattedValue = formatSsnNumber(value);

    setValue("ssn", formattedValue, constants.form);
  };

  const handleSubmit = async ({ ssn }: SsnFormValues) => {
    try {
      const state = user?.userAddress?.state;
      const country = user?.userAddress?.country;

      if (state && country) {
        await setUserTaxProfile({
          variables: {
            taxId: ssn,
            taxIdType: UserTaxIdType.UsaSsn,
            stateOfTaxResidence: state,
            countryOfTaxResidence: country,
          },
        });

        resetToNext();
      } else {
        showErrorBanner();
      }
    } catch {
      showErrorBanner();
    }
  };

  const props: OnboardingLayoutProps = {
    cta: {
      primary: {
        title: i18n.t(Translations.SsnRoutePrimaryCTA),
        onClick: handleFormSubmit(handleSubmit),
        loading,
        disabled: !isValid,
      },
    },
    footerStyle: ssnRouteStyles.footer,
    footer: (
      <div style={ssnRouteStyles.footerContent}>
        <Lock size={16} fill={theme.text.tertiary} />
        <Caption style={{ ...ssnRouteStyles.footerText, color: theme.text.secondary }}>
          {i18n.t(Translations.SsnRouteFooter)}
        </Caption>
      </div>
    ),
  };

  if (userLoading) return <Loader />;
  if (userError) return <ErrorComponent />;

  return (
    <OnboardingLayout {...props}>
      <TextHighlighter
        text={i18n.t(Translations.SsnRouteTitle)}
        words={[{ text: i18n.t(Translations.SsnRouteTitleHighlight) }]}
        TypographyComponent={H2}
        highlightColor={theme.text.highlight}
        style={{
          fontFamily: `"Playfair Display", Georgia, serif`,
          fontWeight: 400,
          fontSize: 34,
          lineHeight: 1.28,
          letterSpacing: 0.2,
        }}
      />
      <Spacing size={SpacingSizes.M} />
      <BodySmall>{i18n.t(Translations.SsnRouteSubtitle)}</BodySmall>
      <Spacing size={SpacingSizes.M} />
      <TextInput
        {...ssnController}
        label={i18n.t(Translations.SsnRouteInputLabel)}
        placeholder={i18n.t(Translations.SsnRouteInputPlaceholder)}
        value={watch("ssn")}
        onChangeText={onChangeText}
        error={errors.ssn?.message}
        inputLeftIcon={<Lock size={16} fill={theme.text.tertiary} />}
      />
      <Spacing size={SpacingSizes.M} />

      <SsnDisclaimer subtitle={i18n.t(Translations.SsnRouteDisclaimerSubtitle)} />
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
